// useSyncLayerOrdering.ts - Custom hook to sync layer ordering changes back to settings

import { useEffect, useRef } from 'react';
import { useLayerOrderingSettings } from './useLayerOrdering';
import { LayerItem, useLayers } from '@ais-ui/react-openlayers';
import { isShapeShownOnStartUp } from '../../../Shapes/isShapeShownOnStartUp';
import { Settings } from '@ais-ui/websockets';
import { areSourceIdentifiersShowOnStartUp } from '../../Coverage/areSourceIdentifiersShownOnStartUp';
import {
  EnabledWMSLayer,
  isWmsLayerShownOnStartUp,
} from '../../../WMSLayers/useWMSSettings';
import { useSettings } from '../../../Settings/SettingsProvider';

const isFavoriteLayer = (settings: Settings, layer: LayerItem): boolean => {
  const isFavoriteShape = isShapeShownOnStartUp(settings, layer.id);
  const isFavoriteWMS = isWmsLayerShownOnStartUp(settings, layer.id);
  const isFavoriteCoverage = areSourceIdentifiersShowOnStartUp(settings);
  const isVesselsLayer = layer.name.toLowerCase() === 'vessels';

  return isFavoriteShape || isFavoriteWMS || isFavoriteCoverage || isVesselsLayer;
};

const getEnabledWmsLayersFromSettings = (settings: Settings) => {
  const wmsInfos = settings.wmsSettings || [];
  const enabled: EnabledWMSLayer[] = [];

  wmsInfos.forEach((wmsInfo: any) => {
    if (wmsInfo.layers) {
      wmsInfo.layers.forEach((layer: any) => {
        if (layer.enabled) {
          enabled.push({ wmsInfo, wmsLayerInfo: layer });
        }
      });
    }
  });

  return enabled;
};

// Helper to safely get layer type for settings
const getLayerTypeForSettings = (
  layer: LayerItem,
  settings: Settings
): string | undefined => {
  // Be very careful with type mapping to avoid corruption
  if (layer.name.toLowerCase() === 'vessels') return 'vessels';

  // Check layer type first
  switch (layer.type) {
    case 'linestring':
    case 'polygon':
    case 'point':
    case 'circle':
      return 'shape';
    case 'wms':
      return 'wms';
    case 'coverage':
    case 'source-identifiers':
      return 'coverage';
    case 'gps-lost-grid':
      return 'gps-lost';
  }

  // More robust WMS detection
  const source = (layer.layer as any)?.getSource?.();
  if (source) {
    // Check for TileWMS source methods/properties instead of constructor name
    if (source.getUrls || source.getUrl || source.getParams) {
      return 'wms';
    }

    // Check source class string representation
    const sourceString = source.toString();
    if (sourceString.includes('TileWMS') || sourceString.includes('ImageWMS')) {
      return 'wms';
    }
  }

  // Check if layer ID matches known WMS layers from settings
  // This is a fallback that should be reliable
  const enabledWmsLayers = getEnabledWmsLayersFromSettings(settings);
  if (enabledWmsLayers.some((wms) => wms.wmsLayerInfo.id === layer.id)) {
    return 'wms';
  }

  return undefined;
};

// Helper to check if layer has a valid type for settings
const hasValidTypeForSettings = (
  layer: LayerItem,
  settings: Settings
): boolean => {
  return getLayerTypeForSettings(layer, settings) !== undefined;
};

// Custom hook to sync layer ordering changes back to settings
export const useSyncLayerOrdering = () => {
  const { layers } = useLayers();
  const { saveLayerOrderingToSettings, getExistingLayerOrdering } =
    useLayerOrderingSettings();
  const { settings } = useSettings();

  // Keep track of the previous layer order to detect changes
  const previousLayerOrderRef = useRef<string>('');
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Skip if no layers yet or during initial load
    if (layers.length === 0) {
      return;
    }

    // Filter layers that are both favorite AND have valid types
    const validLayers = layers.filter(
      (layer) =>
        isFavoriteLayer(settings, layer) &&
        hasValidTypeForSettings(layer, settings)
    );

    // Create a simple string representation of current layer order for comparison
    const currentLayerOrder = validLayers
      .map(
        (layer) =>
          `${layer.id}:${layer.isVisible}:${getLayerTypeForSettings(
            layer,
            settings
          )}`
      )
      .join('|');

    // Skip if no change detected
    if (previousLayerOrderRef.current === currentLayerOrder) {
      return;
    }

    // Skip if this is the first time (no previous order to compare)
    if (previousLayerOrderRef.current === '') {
      previousLayerOrderRef.current = currentLayerOrder;
      return;
    }

    // Debounce the sync to avoid too many saves during rapid changes
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      try {
        // Create new ordering based on current layer state
        const newOrdering = validLayers
          .map((layer) => {
            const type = getLayerTypeForSettings(layer, settings);
            // Double-check type is defined (TypeScript guard)
            if (!type) return null;

            return {
              type,
              id: layer.id,
              enabled: layer.isVisible,
            };
          })
          .filter((item): item is NonNullable<typeof item> => item !== null);

        // Only save if we have a meaningful ordering
        if (newOrdering.length > 0) {
          saveLayerOrderingToSettings(newOrdering);
        }

        previousLayerOrderRef.current = currentLayerOrder;
      } catch (error) {
        console.error('Error syncing layer ordering:', error);
      }
    }, 1000);

    // Cleanup timeout on unmount or change
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [layers, saveLayerOrderingToSettings, getExistingLayerOrdering, settings]);
};
